/**
 * Try/catch wrapper around a Storage for restricted environments (cross-origin iframes,
 * Safari private mode), where even *reading* `window.sessionStorage` throws.
 *
 * The store is resolved per call rather than captured, so a stubbed `window` in tests is
 * picked up rather than frozen at module load.
 */
const safeStorage = (pick: () => Storage) => ({
    getItem(key: string): string | null {
        try {
            return pick().getItem(key);
        } catch {
            return null;
        }
    },
    setItem(key: string, value: string): void {
        try {
            pick().setItem(key, value);
        } catch {
            // Storage unavailable or full
        }
    },
    removeItem(key: string): void {
        try {
            pick().removeItem(key);
        } catch {
            // Storage unavailable
        }
    },
});

export const safeSessionStorage = safeStorage(() => window.sessionStorage);

/**
 * Only the resume pointer uses this, and only with granted consent — see `RESUME_PREFIX`.
 * Everything else the session keeps is tab-scoped on purpose.
 */
export const safeLocalStorage = safeStorage(() => window.localStorage);

/** Read user traits from sessionStorage first, falling back to localStorage (where Segment writes directly when Proxy is unavailable). */
export const safeGetUserTraits = (key: string): string | null =>
    safeSessionStorage.getItem(key) ?? safeLocalStorage.getItem(key);
